import Layout from './layout/Layout'
import './css/adminlte.css'
import './css/App.css'
import { Azure, ApiGateway } from './config/config'
import { createContext, useState, useEffect } from 'react'
import { AzureAuthenticator } from 'tis-azure-auth'
import axios from 'axios'
import { hasAnyRole } from './auth/role'

export const UserContext = createContext(null)
export const AuthContext = createContext(null)

function getEmailFromJwt(jwt = {}) {
  return jwt.mail || jwt.preferred_username || jwt.upn || jwt.email || jwt.unique_name || ''
}

function getKoIdFromJwt(jwt = {}) {
  return jwt.koId || jwt.koid || ''
}

function getKoIdFromProfile(profile = {}) {
  const koIdKeys = [
    'koId',
    'koid',
    'ko_id',
    'onPremisesSamAccountName',
    'onpremisessamaccountname',
  ]

  const findKoId = (value) => {
    if (!value) return ''

    if (Array.isArray(value)) {
      for (const item of value) {
        const koId = findKoId(item)
        if (koId) return koId
      }
      return ''
    }

    if (typeof value !== 'object') return ''

    for (const key of koIdKeys) {
      const koId = value[key]
      if (typeof koId === 'string' && koId.trim()) return koId.trim()
    }

    for (const nestedValue of Object.values(value)) {
      const koId = findKoId(nestedValue)
      if (koId) return koId
    }

    return ''
  }

  return findKoId(profile)
}

function createFallbackUser(jwt = {}, authResponse = {}) {
  const email = getEmailFromJwt(jwt)
  return {
    koId: getKoIdFromJwt(jwt),
    email,
    firstName: jwt.given_name || jwt.name || '',
    lastName: jwt.family_name || '',
    roles: [],
    idToken: authResponse?.idToken,
    accessToken: authResponse?.accessToken,
    hasAnyRole: () => false
  }
}

function getEmailFromAuthResponse(authResponse = {}) {
  return (
    authResponse?.account?.username ||
    authResponse?.username ||
    authResponse?.userName ||
    ''
  )
}

function getEmployeeBaseCandidates() {
  const bases = [ApiGateway.employee]
  const derivedFromPfas = (ApiGateway.pfas || '').replace('pfas-api.', 'sra-api.') + '/employees'
  if (derivedFromPfas && !bases.includes(derivedFromPfas)) {
    bases.push(derivedFromPfas)
  }
  return bases.filter(Boolean)
}

async function fetchEmployeeProfileByEmail(email, employeeOptions) {
  if (!email) return null

  const encodedEmail = encodeURIComponent(email)
  const endpoints = getEmployeeBaseCandidates().map(
    (base) => `${base}/status/active/email/${encodedEmail}`
  )

  for (const endpoint of endpoints) {
    try {
      const response = await axios.get(endpoint, employeeOptions)
      const data = response?.data || null
      const extractedKoId = getKoIdFromProfile(data)
      const topLevelKeys =
        data && typeof data === 'object' && !Array.isArray(data)
          ? Object.keys(data).slice(0, 12)
          : []

      console.info('Employee lookup succeeded', {
        endpoint,
        status: response?.status,
        koId: extractedKoId || null,
        topLevelKeys,
      })

      return data
    } catch (error) {
      console.warn('Employee lookup for koId failed', {
        endpoint,
        status: error?.response?.status,
        message: error?.message
      })
    }
  }

  return null
}

async function fetchUserProfile(jwt, pfasOptions, employeeOptions, emailHint = '') {
  const email = getEmailFromJwt(jwt) || emailHint
  let koIdClaim = getKoIdFromJwt(jwt)
  let employeeProfileByEmail = null

  // Test tokens may not contain koId; resolve it from employee profile first.
  if (!koIdClaim && email) {
    try {
      employeeProfileByEmail = await fetchEmployeeProfileByEmail(email, employeeOptions)
      koIdClaim = getKoIdFromProfile(employeeProfileByEmail)
      console.info('Resolved koId from employee lookup', { koId: koIdClaim || null })
    } catch (error) {
      console.warn('Employee lookup for koId failed', {
        status: error?.response?.status,
        message: error?.message
      })
    }
  }

  if (koIdClaim) {
    const koId = encodeURIComponent(koIdClaim)
    try {
      const response = await axios.get(`${ApiGateway.pfas}/profile/${koId}`, pfasOptions)
      if (response?.data) {
        console.info('Profile endpoint succeeded', {
          endpoint: `${ApiGateway.pfas}/profile/${koId}`,
          koId: koIdClaim,
        })
        return response.data
      }
    } catch (error) {
      console.warn('Profile endpoint failed', {
        endpoint: `${ApiGateway.pfas}/profile/${koId}`,
        status: error?.response?.status,
        message: error?.message
      })
    }
  }

  console.warn('Skipping profile endpoint because koId is not resolved', {
    email: email || null,
    hasEmployeeProfile: !!employeeProfileByEmail,
  })

  if (employeeProfileByEmail) {
    console.info('Using employee profile fallback after profile call', {
      koId: getKoIdFromProfile(employeeProfileByEmail) || null,
    })
    return employeeProfileByEmail
  }

  if (email) {
    try {
      const employeeProfile = await fetchEmployeeProfileByEmail(email, employeeOptions)
      return employeeProfile
    } catch (error) {
      console.warn('Employee fallback endpoint failed', {
        status: error?.response?.status,
        message: error?.message
      })
    }
  }

  return null
}

function parseJwt (token) {
  if(!token) {
    throw new Error("Trying to parse a jwt token, but no token was provided.")
  }
  const payload = token.split('.')[1]
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
  const decoded = decodeURIComponent(
    window.atob(normalized)
      .split('')
      .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
      .join('')
  )
  return JSON.parse(decoded)
}

function App() {
  const [accessToken, setAccessToken] = useState()
  const [rawIdToken, setRawIdToken] = useState()
  const [user, setUser] = useState()
  const [authError, setAuthError] = useState('')

  useEffect(() => {
    if (!accessToken) {
      (async () => {
        try {
          const auth = new AzureAuthenticator(Azure)
          const authResponse = await auth.authenticate()
          if (!authResponse) {
            // loginRedirect can be initiated in authenticate(); wait for redirect callback.
            setAuthError('')
            return
          }
          if (!authResponse?.idToken) {
            throw new Error('Authentication completed but no idToken was returned.')
          }

          const buildOptions = (subscriptionKey) => ({
            headers: {
              // Keep only x-api-key to avoid CORS/preflight issues from extra custom headers.
              'x-api-key': subscriptionKey,
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authResponse?.idToken}`
            }
          })

          const pfasOptions = buildOptions(ApiGateway.pfasApiKey || ApiGateway.tisApiKey)
          const employeeOptions = buildOptions(ApiGateway.tisApiKey || ApiGateway.pfasApiKey)
          setRawIdToken(authResponse?.idToken)

          const jwt = parseJwt(authResponse?.idToken)
          const emailHint = getEmailFromAuthResponse(authResponse)
          let resolvedUser = null

          try {
            resolvedUser = await fetchUserProfile(jwt, pfasOptions, employeeOptions, emailHint)
          } catch (error) {
            console.warn('fetchUserProfile failed before resolving user', {
              status: error?.response?.status,
              message: error?.message
            })
          }

          if (!resolvedUser) {
            resolvedUser = createFallbackUser(jwt, authResponse)
          }

          resolvedUser.hasAnyRole = (roles) => hasAnyRole(resolvedUser.roles || [], roles)
          resolvedUser.idToken = authResponse?.idToken
          resolvedUser.accessToken = authResponse?.accessToken
          setUser(resolvedUser)
          setAccessToken(authResponse?.accessToken || authResponse?.idToken)
          setAuthError('')
        } catch (error) {
          setAuthError(error?.message || 'Authentication failed')
        }
      })()
    }
  }, [accessToken])

  return (
    <>
      {!accessToken && authError && <div>{authError}</div>}
      {accessToken && user && <AuthContext.Provider value={rawIdToken}>
        <UserContext.Provider value={user}>
          <Layout />
        </UserContext.Provider>
      </AuthContext.Provider>}
    </>
  )
}

export default App
